import { setSessionItem } from './session-storage';

export const PRESENTATION_VIEW_STATE_KEY = 'taiPresentationViewState';

export type PresentationViewStateScope = {
  appSessionId: string;
  tenantId: string;
  caseId: string;
  timelineId: string;
};

export type PresentationViewState = {
  selectedSlideId: string;
  openAccordionItems: string[];
  isAccordionVisible: boolean;
  accordionScrollTop: number;
};

type StoredPresentationViewState = Omit<PresentationViewState, 'accordionScrollTop'> & {
  scope: PresentationViewStateScope;
  accordionScrollTop?: number;
};

type PresentationStateStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const NOOP_STORAGE: PresentationStateStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

function defaultStorage(): PresentationStateStorage {
  return typeof sessionStorage !== 'undefined' ? sessionStorage : NOOP_STORAGE;
}

function scopesEqual(
  a: PresentationViewStateScope,
  b: PresentationViewStateScope,
): boolean {
  return (
    a.appSessionId === b.appSessionId &&
    a.tenantId === b.tenantId &&
    a.caseId === b.caseId &&
    a.timelineId === b.timelineId
  );
}

function isScope(value: unknown): value is PresentationViewStateScope {
  if (!value || typeof value !== 'object') return false;
  const scope = value as Partial<PresentationViewStateScope>;
  return (
    typeof scope.appSessionId === 'string' &&
    typeof scope.tenantId === 'string' &&
    typeof scope.caseId === 'string' &&
    typeof scope.timelineId === 'string'
  );
}

function isStoredState(value: unknown): value is StoredPresentationViewState {
  if (!value || typeof value !== 'object') return false;
  const state = value as Partial<StoredPresentationViewState>;
  return (
    isScope(state.scope) &&
    typeof state.selectedSlideId === 'string' &&
    state.selectedSlideId.trim() !== '' &&
    Array.isArray(state.openAccordionItems) &&
    state.openAccordionItems.every((item) => typeof item === 'string') &&
    typeof state.isAccordionVisible === 'boolean' &&
    (state.accordionScrollTop == null ||
      (typeof state.accordionScrollTop === 'number' &&
        Number.isFinite(state.accordionScrollTop) &&
        state.accordionScrollTop >= 0))
  );
}

export function readPresentationViewState(
  scope: PresentationViewStateScope,
  storage: PresentationStateStorage = defaultStorage(),
): PresentationViewState | undefined {
  const raw = storage.getItem(PRESENTATION_VIEW_STATE_KEY);
  if (!raw) return undefined;

  try {
    const stored = JSON.parse(raw) as unknown;
    if (!isStoredState(stored) || !scopesEqual(stored.scope, scope)) {
      storage.removeItem(PRESENTATION_VIEW_STATE_KEY);
      return undefined;
    }
    return {
      selectedSlideId: stored.selectedSlideId,
      openAccordionItems: stored.openAccordionItems,
      isAccordionVisible: stored.isAccordionVisible,
      accordionScrollTop: stored.accordionScrollTop ?? 0,
    };
  } catch {
    storage.removeItem(PRESENTATION_VIEW_STATE_KEY);
    return undefined;
  }
}

/**
 * Persist the view state, or skip it when the session store is full.
 *
 * This runs from an effect on every slide, accordion and scroll change, so an
 * unguarded write turned a full ``sessionStorage`` into a
 * ``QuotaExceededError`` thrown into the error boundary — the whole
 * presentation replaced by "Application error" over a restore convenience.
 * Losing the restore point is the correct failure here.
 */
export function writePresentationViewState(
  scope: PresentationViewStateScope,
  state: PresentationViewState,
  storage: PresentationStateStorage = defaultStorage(),
): boolean {
  return setSessionItem(
    storage,
    PRESENTATION_VIEW_STATE_KEY,
    JSON.stringify({ scope, ...state } satisfies StoredPresentationViewState),
  );
}

export function clearPresentationViewState(
  storage: Pick<Storage, 'removeItem'> = defaultStorage(),
): void {
  storage.removeItem(PRESENTATION_VIEW_STATE_KEY);
}
